import Alpine from "alpinejs";

const CREATED = 1;
const UPDATED = 2;
const EVENT_NAME_REGEX = /^[A-Za-z][A-Za-z0-9]*$/;

var evtSource = null;

function _collectEvents(parent) {
  /**
   * Collects all event IDs along with their associated initial data.
   * If multiple entries share the same event ID, their data is merged.
   */
  const events = {};
  parent = parent || document;

  parent.querySelectorAll("script[data-event-name]").forEach((element) => {
    const eventName = element.getAttribute("data-event-name") || "";

    if (!EVENT_NAME_REGEX.test(eventName)) {
      console.log("Missing or invalid event name");
      return;
    }

    try {
      const initData = JSON.parse(element.innerHTML);

      if (Array.isArray(initData)) {
        // Merge array data
        events[eventName] = [...(events[eventName] ?? []), ...initData];
      } else if (typeof initData === "object") {
        // Merge object data
        events[eventName] = { ...(events[eventName] ?? {}), ...initData };
      } else {
        console.log(`Invalid data type for: ${eventName}`);
      }
    } catch (error) {
      console.log(`Event (${eventName}) parse error: ${error}`);
    }
  });

  return Object.entries(events);
}

function _createOrUpdateStoreData(eventName, data, onlyAdd = false) {
  /**
  * Create or update an Alpine store.
  *
  * - Creates the store if it does not exist.
  * - For arrays: updates items by `uid`, merging `data` or adding new items.
  * - For objects: merges and replaces the store.
  *
  * Merge behavior:
  * - onlyAdd = false -> incoming values overwrite existing
  * - onlyAdd = true -> only new fields are added (existing preserved)
  *
  * Note: shallow merge only (no nested object support).
  */
  const storeData = Alpine.store(eventName);
  if (typeof storeData === "undefined") {
    // Store doesn't exist, create new store
    Alpine.store(eventName, data);
    return CREATED;
  } else if (Array.isArray(storeData) && Array.isArray(data)) {
    data.forEach((item) => {
      const storeItem = storeData.find((e) => e.uid === item.uid);
      if (typeof storeItem === "object") {
        // Merge data for item with same uid
        storeItem.data = onlyAdd
          ? { ...item.data, ...storeItem.data }   // preserve existing
          : { ...storeItem.data, ...item.data };  // overwrite
      } else {
        storeData.push(item);
      }
    });
    return UPDATED;
  } else if (typeof storeData === "object" && typeof data === "object") {
    Alpine.store(eventName, onlyAdd
      ? { ...data, ...storeData }   // preserve existing
      : { ...storeData, ...data }   // overwrite
    );
    return UPDATED;
  } else {
    console.log("Invalid data, expect an object or list");
    return null;
  }
}

function _initSseLifecycleListeners() {
  /**
  * Initialize global event listeners for managing SSE connections and dynamic updates.
  *
  * Ensures the client stays in sync with server-sent events across navigation
  * changes and browser lifecycle events.
  */
  window.addEventListener("beforeunload", () => {
    // Fix: Firefox can’t establish a connection to the server
    if (evtSource !== null) {
      evtSource.close();
    }
  });

  document.addEventListener("visibilitychange", () => {
    // Mobile browsers may suspend inactive tabs, which can close the
    // EventSource connection. To avoid missing events, the page is reloaded
    // when the connection is lost.
    if (evtSource !== null && !document.hidden && evtSource.readyState === 2) {
      location.reload();
    }
  });

  document.addEventListener("htmx:afterSwap", (event) => {
    // On htmx swaps, extracts event definitions, updates Alpine stores, and
    // (re)starts the SSE listener when new events are detected.
    const events = _collectEvents(event.target);
    var newEvents = false;
    events.forEach(([eventName, initData]) => {
      newEvents = newEvents || _createOrUpdateStoreData(eventName, initData, true) === CREATED;
    });
    if (newEvents) {
      _connectSSE(_collectEvents());
    }
  });
}

function _connectSSE(events) {
  /**
  * Establish or replace an SSE connection for the given events.
  */
  if (evtSource !== null) {
    evtSource.close();
  }

  if (events.length > 0) {
    const baseUrl = document.body.getAttribute("data-sse-url");
    const url =
      baseUrl +
      "?events=" +
      events.map((item) => item[0]).join(",");

    evtSource = new EventSource(url);
    evtSource.onopen = () => {
      Alpine.store("sseActive", true);
    };

    evtSource.onerror = () => {
      Alpine.store("sseActive", false);
    };
    events.forEach(([eventName, initData]) => {
      evtSource.addEventListener(eventName, (event) => {
        const data = JSON.parse(event.data);
        _createOrUpdateStoreData(eventName, data);
      });
    })
  }
}

export function startSSE() {
  /**
  * Initialize Server-Sent Events (SSE) for the application.
  */
  try {
    Alpine.store("sseActive", false);
    const events = _collectEvents();
    events.forEach(([eventName, initData]) => {
      _createOrUpdateStoreData(eventName, initData);
    });
    _connectSSE(events);
    _initSseLifecycleListeners();
  } catch (er) {
    console.log(er);
  }
}
